// ─── REC (Reclamation) Workflow Models (M010) ───────────────────────────────

import type { ConservationStatus } from './rl-workflow';

export interface RecCaseListQuery {
  page?: number;
  pageSize?: number;
  stage?: string;
  search?: string;
  fot?: string;
  assetType?: 'Well' | 'Facility' | string;
  sort?: string;
  sortDescending?: boolean;
}

export interface RecCasePage {
  items: RecCaseSummary[];
  totalCount: number;
}

export interface RecCaseSummary {
  id: number;
  workflowCaseId: number;
  caseNumber: string;
  caseStatus: string;
  assetType: 'Well' | 'Facility' | string;
  assetName?: string | null;
  api14?: string | null;
  createdBy?: string | null;
  fotId?: number | null;
  fot?: string | null;
  deadlineDate: string;
  seedingDeferralDate?: string | null;
  seedingDeferralComment?: string | null;
  fanSubmissionOnUtc?: string | null;
  isSkipPath: boolean;
  createdOnUtc: string;
  updatedOnUtc: string;
  state?: string | null;
  reclamationCrew?: string | null;
  queueOrder?: number | null;
}

export interface RecCaseDetail extends RecCaseSummary {
  comments?: string | null;
  cancellationReason?: string | null;
  cancellationComments?: string | null;
  seedingOverrideIsRequired?: boolean | null;
  isSeedingOverrideEvaluated: boolean;
  reclamationJobPlanAttachmentId?: number | null;
  wellAsset?: RecWellAsset | null;
  facilityAsset?: RecFacilityAsset | null;
  digPlan?: RecDigPlan | null;
  businessPartners: RecBusinessPartner[];
  schedulingData?: RecSchedulingData | null;
  seedingSchedulingData?: RecSeedingSchedulingData | null;
  executionData?: RecExecutionData | null;
  seedingExecutionData?: RecSeedingExecutionData | null;
  siteInspection?: RecSiteInspection | null;
  closeOutData?: RecCloseOutData | null;
}

export interface RecWellAsset {
  id: number;
  workflowCaseId: number;
  wellName: string;
  api14: string;
  api12: string;
  api10: string;
  fot?: string | null;
  comments?: string | null;
  area?: string | null;
  costCenter?: string | null;
  state?: string | null;
  plantCode?: string | null;
  companyCode?: string | null;
  localReferenceNumber?: string | null;
  failureDate?: string | null;
  oilBopd?: number | null;
  waterBwpd?: number | null;
  gasMcfpd?: number | null;
  latitude?: number | null;
  longitude?: number | null;
  conservationStatus?: ConservationStatus | null;
}

export interface RecFacilityAsset {
  id: number;
  workflowCaseId: number;
  facilityName: string;
  facilityId?: string | null;
  fot?: string | null;
  comments?: string | null;
  state: string;
  latitude: number;
  longitude: number;
  reasonForDecommission?: string | null;
  directionsToFacility?: string | null;
}

export interface RecDigPlan {
  id: number;
  workflowCaseId: number;
  digPlanAttachmentId?: number | null;
  approvalStatus?: string | null;
  approvalComments?: string | null;
  rejectionComments?: string | null;
  rejectedByName?: string | null;
  rejectedOnUtc?: string | null;
  previousRejectionComments?: string | null;
  previousRejectedOnUtc?: string | null;
  previousRejectedByName?: string | null;
  approvedByUserId?: number | null;
  approvedOnUtc?: string | null;
}

export interface RecBusinessPartner {
  id: number;
  workflowCaseId: number;
  email: string;
  displayOrder: number;
}

export interface RecAttachment {
  id: number;
  caseId: number;
  attachmentType: string;
  fileName: string;
  uploadedDate: string;
  fileSizeBytes: number;
  uploadedBy?: string | null;
  createdBy?: string | null;
  recipient?: string | null;
  notification?: string | null;
}

export interface RecSchedulingData {
  id: number;
  workflowCaseId: number;
  projectedJobDays?: number | null;
  isSetDatesManually: boolean;
  projStartDate?: string | null;
  projEndDate?: string | null;
  landOwnership?: string | null;
  isSkipReclamation: boolean;
  reclamationCrew?: string | null;
  queueOrder?: number | null;
}

export interface RecSeedingSchedulingData {
  id: number;
  workflowCaseId: number;
  seedMix?: string | null;
  projectedJobDays?: number | null;
  isSetDatesManually: boolean;
  projStartDate?: string | null;
  projEndDate?: string | null;
  seedingCrew?: string | null;
  queueOrder?: number | null;
}

export interface RecExecutionData {
  id: number;
  workflowCaseId: number;
  isReclamationCompleted: boolean;
  isNeedsSeeding: boolean;
  acreage?: number | null;
  reclamationCompletedDate?: string | null;
  seedingDeferralDate?: string | null;
  seedingDeferralComment?: string | null;
  comments?: string | null;
  wbsComments?: string | null;
  wbsEntries: RecWbsEntry[];
}

export interface RecWbsEntry {
  id: number;
  recExecutionDataId: number;
  wbsNumber: string;
  percentage: number;
  displayOrder: number;
}

export interface RecSeedingExecutionData {
  id: number;
  workflowCaseId: number;
  seedingCompletedOnUtc?: string | null;
  seedingMethod?: string | null;
  seedingComments?: string | null;
}

export interface RecSiteInspection {
  id: number;
  workflowCaseId: number;
  isSiteReviewCompleted: boolean;
  approvalResult?: string | null;
  reviewComments?: string | null;
  submissionDate?: string | null;
  inspectionReportAttachmentId?: number | null;
  submittedFanAttachmentId?: number | null;
  fanSubmissionOnUtc?: string | null;
  reworkDestination?: string | null;
  approvedByUserId?: number | null;
  approvedOnUtc?: string | null;
  rejectedByUserId?: number | null;
  rejectedOnUtc?: string | null;
}

export interface RecCloseOutData {
  id: number;
  workflowCaseId: number;
  comments?: string | null;
  agencyResponseAttachmentId?: number | null;
  approvedFanAttachmentId?: number | null;
  isSendBackToPreSchedule: boolean;
  moveToStage?: string | null;
  recycleComments?: string | null;
  closedOutByUserId?: number | null;
  closedOutOnUtc?: string | null;
}

export interface SeedingStateRule {
  id: number;
  stateCode: string;
  isSeedingMandatory: boolean;
}

export interface RecLookupOption {
  value: string;
  label: string;
  id?: number;
}

/** One row in the "Select Queue Order" table, returned by GET /api/rec-cases/crew-queue. */
export interface RecCrewQueueRow {
  id: number;
  caseNumber: string;
  assetName: string;
  area: string | null;
  queueOrder: number | null;
  projStartDate: string | null;
  projEndDate: string | null;
  projectedJobDays: number | null;
  deadlineDate: string;
}

/** One item in a batch queue-order update sent to PUT /api/rec-cases/crew-queue. */
export interface UpdateRecCrewQueueItem {
  recCaseId: number;
  queueOrder: number;
}

export interface CreateRecCaseRequest {
  caseNumber: string;
  fotId?: number | null;
  deadlineDate: string;
  comments?: string | null;
  isSkipPath: boolean;
  seedingOverrideIsRequired?: boolean | null;
  wellAsset: CreateRecWellAssetRequest;
}

export interface CreateRecFacilityCaseRequest {
  caseNumber: string;
  fotId?: number | null;
  deadlineDate: string;
  comments?: string | null;
  isSkipPath: boolean;
  seedingOverrideIsRequired?: boolean | null;
  facilityAsset: CreateRecFacilityAssetRequest;
}

export type CreateRecWellAssetRequest = Omit<RecWellAsset, 'id' | 'workflowCaseId' | 'api12' | 'api10'>;
export type UpdateRecWellAssetRequest = CreateRecWellAssetRequest;
export type SaveRecWellAssetRequest = UpdateRecWellAssetRequest;
export type CreateRecFacilityAssetRequest = Omit<RecFacilityAsset, 'id' | 'workflowCaseId'>;
export type UpdateRecFacilityAssetRequest = CreateRecFacilityAssetRequest;
export type SaveRecFacilityAssetRequest = UpdateRecFacilityAssetRequest;

export interface SaveRecDigPlanRequest {
  digPlanAttachmentId?: number | null;
  approvalStatus?: string | null;
  approvalComments?: string | null;
  rejectionComments?: string | null;
}

export interface ApproveDigPlanRequest {
  approvalComments?: string | null;
}

export interface RejectDigPlanRequest {
  rejectionComments: string;
}

export interface SaveRecBusinessPartnerRequest {
  email: string;
  displayOrder: number;
}

export type SaveRecSchedulingDataRequest = Omit<RecSchedulingData, 'id' | 'workflowCaseId'>;
export type SaveRecSeedingSchedulingDataRequest = Omit<RecSeedingSchedulingData, 'id' | 'workflowCaseId'>;
export type SaveRecExecutionDataRequest = Omit<RecExecutionData, 'id' | 'workflowCaseId' | 'wbsEntries'>;
export type SaveRecWbsEntryRequest = Omit<RecWbsEntry, 'id' | 'recExecutionDataId'>;
export type SaveRecSeedingExecutionDataRequest = Omit<RecSeedingExecutionData, 'id' | 'workflowCaseId'>;
export type SaveRecSiteInspectionRequest = Omit<RecSiteInspection, 'id' | 'workflowCaseId' | 'approvedByUserId' | 'approvedOnUtc' | 'rejectedByUserId' | 'rejectedOnUtc'>;
export type SaveRecCloseOutDataRequest = Omit<RecCloseOutData, 'id' | 'workflowCaseId' | 'closedOutByUserId' | 'closedOutOnUtc'>;

export interface ApproveSiteInspectionRequest {
  reviewComments?: string | null;
}

export interface RejectSiteInspectionRequest {
  reviewComments?: string | null;
  reworkDestination: string;
}

export interface RecycleCloseOutRequest {
  moveToStage: string;
  recycleComments: string;
}

export interface CancelRecCaseRequest {
  reason: string;
  comments: string;
}

export interface ReworkRoutingRequest {
  moveToStage: string;
  comments: string;
}
